import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { CORPORATE_STRUCTURE_OPTIONS, FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AvatarImageUploadField,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useNotification } from 'src/hooks/app';
import { useFileUpload } from 'src/hooks/file';
import { useCurrentUserPeoPayrollProvider } from 'src/hooks/peoPayrollProvider';
import { validateWithYupSchema } from 'src/utils/yup';

import * as styles from './styles';
import {
  CompanyInformationFormPeoPayrollProviderFields,
  CompanyInformationFormPeoPayrollProviderValues,
} from './types';
import { getInitialValues } from './utils/getInitialValues';
import { companyInformationFormPeoPayrollProviderValidationSchema } from './utils/validation';

export const CompanyInformationFormPeoPayrollProvider: React.FC = () => {
  const { createApolloContext } = useNotification();

  const { peoPayrollProvider, isPartnership, updatePeoPayrollProvider } =
    useCurrentUserPeoPayrollProvider();
  const { uploadFile } = useFileUpload();

  const initialValues = React.useMemo(
    () => (peoPayrollProvider ? getInitialValues(peoPayrollProvider) : {}),
    [peoPayrollProvider],
  );

  const handleSubmit = React.useCallback(
    async (formData: CompanyInformationFormPeoPayrollProviderValues) => {
      try {
        let newLogoId: string | undefined;
        if (formData.logoFile?.file && formData.logoFile.id !== peoPayrollProvider?.logo?.id) {
          const newLogo = await uploadFile(formData.logoFile.file);
          newLogoId = newLogo.id;
        }

        await updatePeoPayrollProvider({
          data: {
            displayName: formData.displayName,
            contactEmail: formData.contactEmail?.toLowerCase() ?? formData.contactEmail,
            contactPhoneNumber: formData.contactPhoneNumber ?? null,
            industry: formData.industry ?? null,
            corporateStructure: formData.corporateStructure ?? null,
            taxYearEndDate: formData.taxYearEndDate ?? null,
            numberOfEmployees: formData.numberOfEmployees,
            fein: formData.fein,
            defaultDisclaimer: formData.defaultDisclaimer,
            headquarters: {
              [!peoPayrollProvider?.headquarters?.id ? 'create' : 'update']: {
                apartment: formData.headquarters?.apartment ?? null,
                city: formData.headquarters?.city ?? null,
                county: formData.headquarters?.county ?? null,
                state: formData.headquarters?.state ?? null,
                street: formData.headquarters?.street ?? null,
                zipCode: formData.headquarters?.zipCode ?? null,
              },
            },
            logo: newLogoId ? { connect: { id: newLogoId } } : undefined,
          },
          context: createApolloContext({
            successMessage: 'Company Information has been successfully updated',
          }),
        });
      } catch (error) {
        console.error(error);
      }
    },
    [
      createApolloContext,
      peoPayrollProvider?.headquarters?.id,
      peoPayrollProvider?.logo?.id,
      updatePeoPayrollProvider,
      uploadFile,
    ],
  );

  return (
    <Form<CompanyInformationFormPeoPayrollProviderValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ submitting: true, pristine: true, valid: true }}
      validate={validateWithYupSchema(companyInformationFormPeoPayrollProviderValidationSchema)}
    >
      {({ form: { reset }, submitting, pristine, valid }) => (
        <Grid container spacing={3}>
          <Grid size={6}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Field
                name={CompanyInformationFormPeoPayrollProviderFields.displayName}
                label="Company Display Name"
                required
                fullWidth
                disabled={submitting}
                component={TextField}
              />

              <Field
                name={CompanyInformationFormPeoPayrollProviderFields.corporateStructure}
                label="Corporate Structure"
                fullWidth
                disabled={submitting}
                options={CORPORATE_STRUCTURE_OPTIONS}
                component={SelectField}
              />
            </Box>
          </Grid>
          <Grid size={4}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Field
                component={MaskedTextField}
                name={CompanyInformationFormPeoPayrollProviderFields.fein}
                label="FEIN"
                mask={FEIN_MASK}
                fullWidth
                disabled={submitting}
                required
              />

              <Field
                name={CompanyInformationFormPeoPayrollProviderFields.industry}
                label="Industry"
                fullWidth
                disabled={submitting}
                options={INDUSTRIES_OPTIONS}
                component={SelectField}
              />
            </Box>
          </Grid>
          <Grid display="flex" alignItems="center" justifyContent="center" size={2}>
            <Field
              name={CompanyInformationFormPeoPayrollProviderFields.logoFile}
              component={AvatarImageUploadField}
              defaultImageUrl={peoPayrollProvider?.logo?.downloadUrl || DEFAULT_AVATAR}
              avatarProps={{ sx: styles.avatarStyles }}
              disabled={submitting}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoPayrollProviderFields.taxYearEndDate}
              label="Tax Year End Date"
              fullWidth
              disabled={submitting}
              component={MaskedTextField}
              inputProps={{
                placeholder: 'MM/DD',
              }}
              mask={TAX_YEAR_END_DATE_MASK}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoPayrollProviderFields.numberOfEmployees}
              label="Number of Employees"
              fullWidth
              disabled={submitting}
              component={NumberField}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoPayrollProviderFields.contactPhoneNumber}
              label="Primary Contact Phone"
              fullWidth
              disabled={submitting}
              component={PhoneField}
            />
          </Grid>
          <Grid size={6}>
            <Field
              name={CompanyInformationFormPeoPayrollProviderFields.contactEmail}
              label="Primary Contact Email"
              required
              fullWidth
              disabled={submitting}
              component={TextField}
            />
          </Grid>

          <Grid spacing={2} size={12}>
            <Grid size={12}>
              <Typography fontWeight={600}>Headquarters address</Typography>
            </Grid>
            <Grid mt={2}>
              <AddressFields
                prefix={CompanyInformationFormPeoPayrollProviderFields.headquarters}
                requiredStreet
                requiredCity
                requiredState
                requiredZipCode
                disabled={submitting}
                addressLabel="Street"
              />
            </Grid>
          </Grid>

          <React.Activity mode={isPartnership ? 'hidden' : 'visible'}>
            <Grid size={12}>
              <DefaultDisclaimerInputSection
                headerText="PEO Payroll Provider Disclaimer"
                name={CompanyInformationFormPeoPayrollProviderFields.defaultDisclaimer}
              />
            </Grid>
          </React.Activity>

          <Grid size={12}>
            <Box display="flex" gap={3} justifyContent="center">
              <Button
                disabled={pristine || submitting}
                variant="outlined"
                onClick={() => reset()}
                color="error"
              >
                Reset
              </Button>
              <Button
                disabled={pristine || submitting || !valid}
                loading={submitting}
                variant="contained"
                type="submit"
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Form>
  );
};
